import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import { syncFilesTokenCookie } from "@src/utils/cookies"

export interface AppState {
  token: string | undefined
  adminToken: string | undefined
  customDnsHost: string
  allowedFileExtensions: string[]
  mustAcceptTos: boolean
  setToken: (newToken: string) => void
  removeToken: () => void
  setAdminToken: (token: string) => void
  clearAdminToken: () => void
  setCustomDnsHost: (host: string) => void
  setAllowedFileExtensions: (exts: string[]) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      token: "",
      adminToken: undefined,
      customDnsHost: "",
      allowedFileExtensions: [],
      mustAcceptTos: false,
      setToken: (newToken: string) => set(() => ({ token: newToken })),
      removeToken: () => set(() => ({ token: "" })),
      setAdminToken: (token: string) => set(() => ({ adminToken: token })),
      clearAdminToken: () => set(() => ({ adminToken: undefined })),
      setCustomDnsHost: (host: string) => set(() => ({ customDnsHost: host })),
      setAllowedFileExtensions: (exts: string[]) =>
        set(() => ({ allowedFileExtensions: exts })),
    }),
    {
      name: "app-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { mustAcceptTos, ...rest } = state
        return rest
      },
    },
  ),
)

// Every token change (login, impersonation, 401, logout) goes through the
// store, so mirroring it here keeps filesToken in step without touching the
// login paths. The store hydrates from localStorage synchronously, so the
// first sync covers sessions that were already logged in.
if (import.meta.env.VITE_ENABLE_FILES_COOKIE === "true") {
  syncFilesTokenCookie(useAppStore.getState().token)
  useAppStore.subscribe((state, prev) => {
    if (state.token !== prev.token) syncFilesTokenCookie(state.token)
  })
  // Staging and production write the same cookie (same name, .babele.co,
  // same path), so logging in on one overwrites the other. Write it again
  // whenever this tab gets focus, so the tab in use has its own token.
  // ponytail: two windows of different environments open side by side still
  // fight; an environment-specific cookie name (needs the API) fixes that.
  window.addEventListener("focus", () =>
    syncFilesTokenCookie(useAppStore.getState().token),
  )
}
